import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import type {
  MiddlewareHealth,
  MiddlewareHealthEvent,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { AlertCircle, AlertTriangle } from 'lucide-react';

interface Props {
  health: MiddlewareHealth | undefined;
  /** Display name of the middleware, e.g. "PNA middleware". */
  label?: string;
  /**
   * Show the "silent for 90+ min" alert. Off for a middleware that reports
   * its schedule: its jobs are judged against that instead, and a daily job
   * is not silent for being quiet all afternoon.
   */
  silenceAlert?: boolean;
}

// 90 minutes — matches STALE_RED_SECONDS in pna_middleware_health.py.
// We only surface the heartbeat-silent alert when ALL sources are this stale,
// because the slowest crons fire hourly so a freshly-running middleware can
// legitimately have a 30-60min "stale" reading on most workers.
const SILENT_THRESHOLD_SECONDS = 90 * 60;

// Severities that count as something the user needs to see.
const NOISY_SEVERITIES = new Set<MiddlewareHealthEvent['severity']>([
  'error',
  'fatal',
  'warning',
]);

// Notes the middleware sends for the record that need nobody's action: rows SAP's
// saved query cannot return are read by the daily open-lines snapshot instead, so
// nothing is missing. They are not issues and are left off this card.
const DATA_NOTE_CODES = new Set<string>(['SAP_UNREADABLE_ROWS']);

const MAX_EVENTS_SHOWN = 8;

/** One row per distinct event: the same source, code and message repeated each run. */
interface GroupedEvent {
  event: MiddlewareHealthEvent;
  count: number;
}

/**
 * Collapses repeats of the same event into one row, keeping the latest occurrence.
 * @param events - events, newest first
 * @returns one entry per distinct source + code + message, newest first
 */
function groupRepeats(events: MiddlewareHealthEvent[]): GroupedEvent[] {
  const byKey = new Map<string, GroupedEvent>();
  for (const e of events) {
    const key = `${e.source}|${e.code}|${e.message}`;
    const seen = byKey.get(key);
    if (seen) {
      seen.count += 1;
      if ((e.occurred_at ?? '') > (seen.event.occurred_at ?? '')) seen.event = e;
    } else {
      byKey.set(key, { event: e, count: 1 });
    }
  }
  return Array.from(byKey.values()).sort((a, b) =>
    (b.event.occurred_at ?? '').localeCompare(a.event.occurred_at ?? ''),
  );
}

/**
 * Renders a middleware's issues as a compact stream when something's wrong.
 *
 * Display logic:
 * - No issues at all → renders null (does not occupy Overview real estate).
 * - Process not heartbeating for 90+ min → single one-line alert (no card chrome).
 * - Recent error/fatal/warning events → compact "Middleware issues" card with
 *   the event stream highlighted via severity-coloured row borders.
 *
 * Hidden entirely when no health data is provided (non-PNA source).
 */
export function MiddlewareHealthCard({ health, label = 'PNA middleware', silenceAlert = true }: Props) {
  const noisyEvents = useMemo(
    () =>
      groupRepeats(
        (health?.recent_events ?? []).filter(
          (e) => NOISY_SEVERITIES.has(e.severity) && !DATA_NOTE_CODES.has(e.code),
        ),
      ),
    [health],
  );

  if (!health) return null;

  const isSilent = silenceAlert && isHeartbeatSilent(health);
  const hasEvents = noisyEvents.length > 0;

  // Healthy default: nothing on screen.
  if (!isSilent && !hasEvents) return null;

  return (
    <div className="mb-4 space-y-2">
      {isSilent && <SilentAlert health={health} label={label} />}
      {hasEvents && <IssueStreamCard events={noisyEvents} />}
    </div>
  );
}

function isHeartbeatSilent(health: MiddlewareHealth): boolean {
  // No sources reporting at all → silent.
  if (health.sources.length === 0) return true;
  // Surface only when EVERY source is stale beyond the red threshold —
  // i.e. the freshest heartbeat is still > 90min old.
  const freshest = health.min_stale_seconds ?? health.max_stale_seconds;
  return freshest !== null && freshest !== undefined && freshest > SILENT_THRESHOLD_SECONDS;
}

function SilentAlert({ health, label }: { health: MiddlewareHealth; label: string }) {
  const stale = health.min_stale_seconds ?? health.max_stale_seconds;
  const message =
    health.sources.length === 0
      ? `${label} has not reported in yet — service may not be running, or auth/network is broken.`
      : `${label} silent for ${formatDuration(stale)} — host service likely stopped.`;
  return (
    <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function IssueStreamCard({ events }: { events: GroupedEvent[] }) {
  const { formatDateTime } = useFormatters();
  const shown = events.slice(0, MAX_EVENTS_SHOWN);
  const hiddenCount = events.length - shown.length;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Middleware issues
          <Badge variant="secondary" className="ml-1 tabular-nums">
            {events.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y">
          {shown.map(({ event: e, count }, i) => (
            <li
              key={`${e.occurred_at ?? 'no-ts'}-${i}`}
              className={`flex gap-3 border-l-4 py-2 pl-3 pr-1 ${severityBorderClass(e.severity)}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs">
                  <Badge
                    variant={severityBadgeVariant(e.severity)}
                    className="uppercase"
                  >
                    {e.severity}
                  </Badge>
                  <span className="font-medium">{e.source}</span>
                  {e.code && (
                    <span className="font-mono text-muted-foreground">
                      {e.code}
                    </span>
                  )}
                  {e.occurred_at && (
                    <span className="ml-auto text-muted-foreground">
                      {count > 1 ? `${count} times, last ` : ''}
                      {formatDateTime(e.occurred_at)}
                    </span>
                  )}
                </div>
                <div className="mt-1 break-words text-sm">{e.message}</div>
                {(e.sap_status_code !== null || e.sap_page !== null) && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {e.sap_status_code !== null && (
                      <span>SAP {e.sap_status_code}</span>
                    )}
                    {e.sap_page !== null && <span> · page {e.sap_page}</span>}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
        {hiddenCount > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            +{hiddenCount} more
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function severityBorderClass(sev: MiddlewareHealthEvent['severity']): string {
  switch (sev) {
    case 'fatal':
      return 'border-l-red-600';
    case 'error':
      return 'border-l-red-500';
    case 'warning':
      return 'border-l-amber-500';
    default:
      return 'border-l-slate-300';
  }
}

function severityBadgeVariant(
  sev: MiddlewareHealthEvent['severity'],
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (sev) {
    case 'fatal':
    case 'error':
      return 'destructive';
    case 'warning':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}
